import type {
  Bonus,
  Differentiator,
  Faq,
  InsideImage,
  Module,
  PainPoint,
  PopupSale,
  Testimonial,
} from '../types/content'

export const PRODUCT_NAME = 'GENERATOR DIAGNOSTIC PRO™'
export const CHECKOUT_URL = 'https://generator-diagnostic-pro.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '757545003709827'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/1UyUEhLBefsDG5QqtKmK5y/Diseno-sin-titulo-14.webp'

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/3gSBfDZxkcFLrQiXZ63X8n/Captura-de-pantalla-2026-10-06-a-la_s_-5.11.59_p._m..png',
    alt: 'Unstable Frequency — Identify the Pattern: stable, slow hunting and rapid instability charts with governor, fuel delivery, load and control diagnostic directions',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/3iAkb3ogy17KXpUmRPi4aY/Captura-de-pantalla-2026-10-06-a-la_s_-5.11.17_p._m..png',
    alt: 'Excitation System Fault Finder: brushless, brush/slip ring and PMG systems with symptom, system area and evidence-to-collect table',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/dJax7uMebb6aDBtbagYgKr/Captura-de-pantalla-2026-10-06-a-la_s_-5.10.31_p._m..png',
    alt: 'Start With the Symptom — Not the Component: five-step observe, test, isolate, diagnose, verify workflow mapped to generator systems',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/sXnDshduFYxyAib2QJPsvm/Captura-de-pantalla-2026-10-06-a-la_s_-5.11.02_p._m..png',
    alt: 'No-Output System Isolation Map: rotor, stator, regulation, protection and output zones with function, failure clue and test direction',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/t15ka8Dcfg9eYmwfqfp853/Captura-de-pantalla-2026-10-06-a-la_s_-5.10.47_p._m..png',
    alt: 'Slow Cranking — Find Where Starting Energy Is Being Lost: seven voltage-drop test points, observation table and temperature effects',
  },
]

export const painPoints: PainPoint[] = [
  {
    title: '😰 ONE SYMPTOM. TOO MANY POSSIBLE CAUSES.',
    body: 'Fuel problem? Starting circuit? Ignition? AVR? Excitation? Wiring? Sensor? Controller? Governor? Load issue? Several different faults can produce similar symptoms, making diagnosis difficult without a structured process.',
  },
  {
    title: '⚠️ THE GENERATOR RUNS—BUT THE PROBLEM IS STILL THERE',
    body: 'The engine may start normally while output voltage is incorrect. It may produce correct voltage with no load but fail when equipment is connected. It may operate manually but fail during automatic operation. Finding the real cause requires more than confirming that the engine runs.',
  },
  {
    title: '📚 YOUR DIAGNOSTIC INFORMATION IS EVERYWHERE',
    body: "Service manuals, wiring diagrams, meter readings, previous notes, controller information, specifications and troubleshooting tables. The information may exist—but it's scattered when you're trying to isolate a fault efficiently.",
  },
  {
    title: '😩 STOP REPLACING PARTS BEFORE CONFIRMING THE FAULT',
    body: 'Replacing a battery, AVR, sensor, relay, starter, fuel component or controller because it “might be the problem” can waste time and money. Build the diagnosis around symptoms, measurements, tests and verified evidence instead.',
  },
]

export const modules: Module[] = [
  {
    number: 1,
    title: 'GENERATOR DIAGNOSTIC FOUNDATIONS & FAULT-FINDING™',
    body: 'Build a systematic troubleshooting process through initial inspection, symptom analysis, mechanical vs. electrical fault isolation, starting checks, fuel checks, wiring inspection, voltage and frequency measurements, continuity testing, diagnostic decision trees and post-repair verification.',
  },
  {
    number: 2,
    title: 'ENGINE, STARTING & FUEL SYSTEM DIAGNOSTICS™',
    body: "Diagnose generators that won't crank, crank but won't start, start and stop, run rough, surge, stall, lose power or shut down unexpectedly through structured checks of starting, fuel, ignition, air, protection and engine-related systems.",
  },
  {
    number: 3,
    title: 'NO POWER, LOW VOLTAGE & OUTPUT DIAGNOSTICS™',
    body: 'Troubleshoot generators that run but produce no power, low voltage, high voltage or unstable output through organized diagnostic paths covering excitation, rotor and stator conditions, brushes, slip rings, diodes, rectifiers, AVR-related circuits, breakers, wiring and output connections.',
  },
  {
    number: 4,
    title: 'FREQUENCY, LOAD & POWER QUALITY DIAGNOSTICS™',
    body: 'Investigate voltage drop, frequency problems, unstable operation, breaker trips, generator bogging, overload conditions, phase imbalance, current imbalance and failures that appear only when real loads are connected.',
  },
  {
    number: 5,
    title: 'CONTROL PANEL, SENSORS & AUTOMATIC START DIAGNOSTICS™',
    body: 'Troubleshoot controller power problems, start-command failures, alarms, shutdowns, sensor-related faults, relays, solenoids, control wiring, remote-start circuits, automatic-start sequences and transfer-related generator symptoms.',
  },
  {
    number: 6,
    title: 'GENERATOR FAULT FINDER & TROUBLESHOOTING SYSTEM™',
    body: 'Move directly from the observed symptom to a structured diagnostic path using fault-finding charts, troubleshooting maps, comparison tables, measurement sheets and decision trees for starting, engine, output, voltage, frequency, load, control and automatic-operation faults.',
  },
]

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'GENERATOR FIELD DIAGNOSTIC WORKBOOK™',
    body: 'Organize each diagnostic job from the original complaint to final verification with ready-to-use worksheets for generator identification, initial inspection, symptoms, measurements, component testing, fault isolation, corrective actions and post-repair checks.',
  },
  {
    number: 2,
    title: 'GENERATOR ELECTRICAL TESTING & MEASUREMENT COMPANION™',
    body: 'Structure electrical testing with practical resources for voltage, frequency, current, resistance, continuity, voltage drop, starting circuits, generator output, excitation, AVR-related checks, sensors and control circuits.',
  },
  {
    number: 3,
    title: 'GENERATOR SYMPTOM-TO-CAUSE QUICK REFERENCE™',
    body: 'Start with the actual generator symptom and quickly organize the next diagnostic direction through symptom-based references covering no-crank, no-start, shutdown, no-output, low or high voltage, unstable frequency, load failures, breaker trips, alarms and automatic-operation problems.',
  },
  {
    number: 4,
    title: 'GENERATOR PREVENTIVE INSPECTION & EARLY-FAULT DETECTION KIT™',
    body: 'Identify developing problems before they become major failures with structured inspections for starting condition, engine systems, electrical connections, generator output, controls, alarms, load response, environmental conditions and standby readiness.',
  },
  {
    number: 5,
    title: 'GENERATOR REPAIR VERIFICATION & SERVICE REPORT PACK™',
    body: 'Document the confirmed fault, corrective action and final operating condition with before-and-after measurements, start/stop verification, load checks, output stability records, automatic-operation checks, service summaries and return-to-service documentation.',
  },
]

export const differentiators: Differentiator[] = [
  {
    icon: 'zap',
    title: 'Practical troubleshooting resources',
    body: 'Find the diagnostic workflow, symptom reference, testing record, fault-finding chart or verification tool you need.',
  },
  {
    icon: 'search',
    title: 'Structured fault-finding system',
    body: 'Move from the reported symptom to inspection, testing, fault isolation, confirmed diagnosis and final repair verification.',
  },
  {
    icon: 'settings',
    title: '6 dedicated diagnostic areas',
    body: 'Diagnostic foundations, engine and starting, electrical output, frequency and load, controls and automatic operation, and symptom-based fault finding.',
  },
  {
    icon: 'gift',
    title: '5 practical professional bonuses',
    body: 'Field documentation, electrical testing, symptom-to-cause references, preventive inspection and repair verification tools.',
  },
  {
    icon: 'refresh',
    title: 'Ready to use again and again',
    body: 'Return to the system whenever you need to diagnose, test, compare measurements, document findings or verify a generator repair.',
  },
]

export const offerBullets = [
  '6 comprehensive diagnostic modules',
  '5 strategic professional bonuses',
  'Engine, starting & fuel-system diagnostics',
  'No-power, voltage & output troubleshooting',
  'Frequency, load & power-quality diagnostics',
  'Control panel, sensor & automatic-start troubleshooting',
  'Symptom-to-cause fault-finding resources',
  'Testing, documentation & repair verification tools',
]

export const testimonials: Testimonial[] = [
  {
    name: 'Mike Donovan, 44',
    role: 'Generator Service Technician',
    location: 'Columbus, United States',
    headline: 'It gives me a much clearer diagnostic path.',
    quote:
      'Instead of jumping from one possible cause to another, I can start with the symptom, organize the tests and eliminate possibilities step by step. It makes troubleshooting much easier to structure.',
  },
  {
    name: 'James Whitaker, 38',
    role: 'Generator Technician',
    location: 'Manchester, United Kingdom',
    headline: 'The symptom-based references are what I needed.',
    quote:
      'When a generator runs but has no output or loses voltage under load, there can be several directions to investigate. Having the possible systems and next checks organized together saves me from working randomly.',
  },
  {
    name: 'Ryan Kowalski, 31',
    role: 'Equipment Service Technician',
    location: 'Edmonton, Canada',
    headline: 'Much better than relying on scattered notes.',
    quote:
      "I used to have measurements, troubleshooting notes and references in different places. Having worksheets and diagnostic paths together gives me a much cleaner process when I'm working through a fault.",
  },
  {
    name: 'Craig Mitchell, 49',
    role: 'Field Service Technician',
    location: 'Brisbane, Australia',
    headline: 'The load troubleshooting section is extremely useful.',
    quote:
      'A generator can look perfectly fine until a load is connected. The system makes it easier to compare voltage, frequency, engine response and other symptoms instead of assuming the first component is bad.',
  },
  {
    name: 'Danielle Ruiz, 36',
    role: 'Generator Maintenance Technician',
    location: 'Phoenix, United States',
    headline: 'A practical system I can keep using.',
    quote:
      "I like that it isn't just something to read once. The diagnostic sheets, inspection tools, fault references and verification resources are the kind of material I can return to whenever I'm troubleshooting a generator.",
  },
]

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Generator technicians, equipment service technicians, maintenance professionals and other qualified technical professionals who diagnose, service or maintain generator systems.',
  },
  {
    q: 'What generator problems does it cover?',
    a: 'The system covers representative faults involving starting, fuel and engine operation, electrical output, low or high voltage, unstable voltage, frequency, load response, breakers, excitation-related systems, controls, sensors, automatic starting and other common generator troubleshooting areas.',
  },
  {
    q: 'Does it include symptom-based troubleshooting?',
    a: 'Yes. Symptom-to-cause fault finding is a central part of the system, with structured diagnostic paths for starting problems, engine behavior, no-output conditions, voltage and frequency faults, load-related failures, alarms and automatic-operation problems.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. GENERATOR DIAGNOSTIC PRO™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is a professional diagnostic and reference system designed to organize generator troubleshooting and service work.',
  },
  {
    q: 'Is it another theory-heavy ebook?',
    a: 'No. The system focuses on diagnostic workflows, troubleshooting paths, measurement records, fault-finding charts, inspection tools, decision-making resources and practical service documentation.',
  },
  {
    q: 'Does this replace manufacturer service information or professional training?',
    a: 'No. Generator specifications, test procedures, wiring configurations, safety requirements and service methods vary by manufacturer and model. Applicable manufacturer documentation, electrical safety requirements, regulations and qualified professional judgment should always be followed.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
  {
    q: 'What devices can I use it on?',
    a: 'Any device that opens PDF files — phone, tablet, laptop or desktop. Keep it on your phone in the field, or print the worksheets, measurement sheets and service reports you use most.',
  },
  {
    q: "What if it isn't right for me?",
    a: "You're covered by the 7-Day Money-Back Guarantee. If it isn't right for you, contact us within 7 days for a full refund according to the refund terms.",
  },
]

export const popupSales: PopupSale[] = [
  { name: 'Mark H.', city: 'Dallas', country: 'United States' },
  { name: 'Tom R.', city: 'Birmingham', country: 'United Kingdom' },
  { name: 'Kevin L.', city: 'Calgary', country: 'Canada' },
  { name: 'Scott B.', city: 'Perth', country: 'Australia' },
  { name: 'Brian W.', city: 'Houston', country: 'United States' },
  { name: 'Gareth P.', city: 'Cardiff', country: 'United Kingdom' },
  { name: 'Derek M.', city: 'Hamilton', country: 'Canada' },
  { name: 'Luke T.', city: 'Melbourne', country: 'Australia' },
  { name: 'Jason K.', city: 'Denver', country: 'United States' },
  { name: 'Neil S.', city: 'Glasgow', country: 'United Kingdom' },
  { name: 'Chris D.', city: 'Winnipeg', country: 'Canada' },
  { name: 'Shane O.', city: 'Adelaide', country: 'Australia' },
]
